/**
 * Generic URL Appender Module
 * Handles appending URL parameters from data-append-url attributes to button/link hrefs on click
 *
 * Features:
 * - Cumulative parameters (e.g., issue=value1,value2)
 * - Replacement parameters (e.g., category=value)
 * - Selective removal (e.g., data-append-url="key=")
 * - Complete reset (e.g., data-append-url="")
 */

/**
 * Error messages for the URL Appender module
 */
const ERROR_MESSAGES = {
  INIT: 'URL Appender: Failed to initialize',
  INVALID_ELEMENT: 'URL Appender: Invalid element encountered',
  URL_PARSE: 'URL Appender: Failed to parse URL',
  REMOVE_ISSUE: 'URL Appender: Failed to remove issue from URL',
};

/**
 * Configuration for parameter behavior
 */
const CONFIG = {
  // Keys that should replace values instead of accumulating them
  replaceKeys: ['category', 'type'],
  // Container selectors for finding view-all anchors
  containerSelectors: ['section', 'main', 'body'],
  // Constants for attribute names and values
  ATTRIBUTES: {
    DATA_APPEND_URL: 'data-append-url',
    HREF: 'href',
    DATA_HREF: 'data-href',
    DATA_ORIGINAL_HREF: 'data-original-href',
  },
  SELECTORS: {
    VIEW_ALL: 'a[class*="view-all"]',
  },
  VALUES: {
    EMPTY_HREF: '#',
    ISSUE_PREFIX: 'issue=',
    COMMA: ',',
    QUERY_SEPARATOR: '?',
    EQUALS: '=',
  },
};

/**
 * Finds the target anchor element for URL modification
 * @param {HTMLElement} element - The clicked element
 * @returns {HTMLElement|null} - The target anchor or null if not found
 */
const findTargetAnchor = element => {
  // If element is already an anchor, use it
  if (element.tagName.toLowerCase() === 'a') {
    return element;
  }

  // Try to find ancestor anchor with "view-all" class
  const ancestorAnchor = element.closest('a[class*="view-all"]');
  if (ancestorAnchor) return ancestorAnchor;

  // Try to find descendant anchor with "view-all" class
  const descendantAnchor = element.querySelector('a[class*="view-all"]');
  if (descendantAnchor) return descendantAnchor;

  // Search in broader container scopes
  for (const containerSelector of CONFIG.containerSelectors) {
    const container = element.closest(containerSelector);
    if (container) {
      const siblingAnchor = container.querySelector('a[class*="view-all"]');
      if (siblingAnchor) return siblingAnchor;
    }
  }

  return null;
};

/**
 * Get current href from an element, checking both href and data-href attributes
 * @param {HTMLElement} element - The element to get href from
 * @returns {string} The current href value
 */
const getCurrentHref = element => {
  return element.getAttribute(CONFIG.ATTRIBUTES.HREF) || element.getAttribute(CONFIG.ATTRIBUTES.DATA_HREF) || '';
};

/**
 * Update href attributes on an element
 * @param {HTMLElement} element - The element to update
 * @param {string} newHref - The new href value
 * @returns {void}
 */
const updateHrefAttributes = (element, newHref) => {
  element.setAttribute(CONFIG.ATTRIBUTES.HREF, newHref);
  if (element.getAttribute(CONFIG.ATTRIBUTES.DATA_HREF)) {
    element.setAttribute(CONFIG.ATTRIBUTES.DATA_HREF, newHref);
  }
};

/**
 * Build URL with query parameters
 * @param {string} baseUrl - The base URL without query string
 * @param {URLSearchParams} urlParams - The URL parameters
 * @returns {string} The complete URL with query string
 */
const buildUrlWithParams = (baseUrl, urlParams) => {
  const finalQueryString = urlParams.toString();
  if (!finalQueryString) return baseUrl;

  // Decode URL-encoded commas for better readability
  const decodedQueryString = finalQueryString.replace(/%2C/g, CONFIG.VALUES.COMMA);
  return baseUrl + CONFIG.VALUES.QUERY_SEPARATOR + decodedQueryString;
};

/**
 * Processes URL parameters based on the append value
 * @param {URLSearchParams} urlParams - Current URL parameters
 * @param {string} appendValue - Value to append/modify
 * @returns {void}
 */
const processUrlParameters = (urlParams, appendValue) => {
  // Handle removal operation (format: "key=")
  if (appendValue.includes(CONFIG.VALUES.EQUALS) && appendValue.endsWith(CONFIG.VALUES.EQUALS)) {
    const keyToRemove = appendValue.slice(0, -1);
    urlParams.delete(keyToRemove);
    return;
  }

  // Parse the new parameter
  const [newKey, newValue] = appendValue.split(CONFIG.VALUES.EQUALS);
  if (!newKey || !newValue) return;

  // Handle replacement vs cumulative behavior
  if (CONFIG.replaceKeys.includes(newKey)) {
    // Replace the value for these keys
    urlParams.set(newKey, newValue);
  } else {
    // Combine values with comma for other keys
    if (urlParams.has(newKey)) {
      const existingValues = urlParams.get(newKey).split(CONFIG.VALUES.COMMA);
      if (!existingValues.includes(newValue)) {
        existingValues.push(newValue);
        urlParams.set(newKey, existingValues.join(CONFIG.VALUES.COMMA));
      }
    } else {
      urlParams.set(newKey, newValue);
    }
  }
};

/**
 * Appends URL parameters to an element's href attribute
 * @param {HTMLElement} element - The element to process
 * @returns {void}
 */
const appendUrlToElement = element => {
  try {
    const appendValue = element.getAttribute(CONFIG.ATTRIBUTES.DATA_APPEND_URL);
    if (appendValue === null) return;

    // Find the target anchor element
    const targetElement = findTargetAnchor(element);
    if (!targetElement) {
      console.warn('URL Appender: No anchor with "view-all" class found', element);
      return;
    }

    // Get current href
    const currentHref = getCurrentHref(targetElement);
    if (!currentHref || currentHref === CONFIG.VALUES.EMPTY_HREF) {
      console.warn('URL Appender: Element has no valid href to append to', targetElement);
      return;
    }

    // Store original href for reset functionality
    if (!targetElement.getAttribute(CONFIG.ATTRIBUTES.DATA_ORIGINAL_HREF)) {
      targetElement.setAttribute(CONFIG.ATTRIBUTES.DATA_ORIGINAL_HREF, currentHref);
    }

    // Handle complete reset
    if (appendValue.trim() === '') {
      const originalHref = targetElement.getAttribute(CONFIG.ATTRIBUTES.DATA_ORIGINAL_HREF);
      if (originalHref) {
        updateHrefAttributes(targetElement, originalHref);
      }
      return;
    }

    // Parse and modify URL parameters
    const [baseUrl, queryString] = currentHref.split(CONFIG.VALUES.QUERY_SEPARATOR);
    const urlParams = new URLSearchParams(queryString || '');

    processUrlParameters(urlParams, appendValue);

    // Build and update the new URL
    const newHref = buildUrlWithParams(baseUrl, urlParams);
    updateHrefAttributes(targetElement, newHref);
  } catch (error) {
    console.error(ERROR_MESSAGES.INVALID_ELEMENT, error, element);
  }
};

/**
 * Handle click events on elements with data-append-url attributes
 * @param {Event} event - The click event
 * @returns {void}
 */
const handleClickWithAppendUrl = event => {
  const element = event.target.closest(`[${CONFIG.ATTRIBUTES.DATA_APPEND_URL}]`);
  if (element) {
    appendUrlToElement(element);
  }
};

/**
 * Remove a specific issue value from the URL's issue parameter
 * @param {string} issueValueToRemove - The issue value to remove
 * @returns {void}
 */
const removeIssueFromUrl = issueValueToRemove => {
  if (!issueValueToRemove) {
    console.warn('URL Appender: No issue value provided for removal');
    return;
  }

  try {
    // Find all "view-all" anchors in the document
    const viewAllAnchors = document.querySelectorAll(CONFIG.SELECTORS.VIEW_ALL);

    viewAllAnchors.forEach(anchor => {
      const currentHref = getCurrentHref(anchor);
      if (!currentHref || currentHref === CONFIG.VALUES.EMPTY_HREF) return;

      // Parse and modify URL parameters
      const [baseUrl, queryString] = currentHref.split(CONFIG.VALUES.QUERY_SEPARATOR);
      const urlParams = new URLSearchParams(queryString || '');

      if (urlParams.has('issue')) {
        const currentIssues = urlParams.get('issue').split(CONFIG.VALUES.COMMA);
        const filteredIssues = currentIssues.filter(issue => issue !== issueValueToRemove);

        if (filteredIssues.length === 0) {
          // Remove the issue parameter entirely if no issues remain
          urlParams.delete('issue');
        } else {
          // Update with remaining issues
          urlParams.set('issue', filteredIssues.join(CONFIG.VALUES.COMMA));
        }

        // Build and update the new URL
        const newHref = buildUrlWithParams(baseUrl, urlParams);
        updateHrefAttributes(anchor, newHref);
      }
    });
  } catch (error) {
    console.error(ERROR_MESSAGES.REMOVE_ISSUE, error);
  }
};

/**
 * Handle change events on checkbox elements with data-append-url attributes
 * This ensures URL parameters are updated when checkboxes are unchecked
 * @param {Event} event - The change event
 * @returns {void}
 */
const handleChangeWithAppendUrl = event => {
  // Only handle checkbox elements
  if (event.target.type !== 'checkbox') return;

  const element = event.target.closest(`[${CONFIG.ATTRIBUTES.DATA_APPEND_URL}]`);
  if (!element) return;

  const appendValue = element.getAttribute(CONFIG.ATTRIBUTES.DATA_APPEND_URL);
  if (!appendValue) return;

  // For issue parameters, we need special handling
  if (appendValue.startsWith(CONFIG.VALUES.ISSUE_PREFIX)) {
    if (!event.target.checked) {
      // When unchecked, we need to remove this specific value from the issue parameter
      const [, issueValue] = appendValue.split(CONFIG.VALUES.EQUALS);
      if (issueValue) {
        removeIssueFromUrl(issueValue);
      }
    } else {
      // When checked, add the parameter normally
      appendUrlToElement(element);
    }
  } else {
    // For non-issue parameters, use normal behavior
    appendUrlToElement(element);
  }
};

/**
 * Initializes URL appending for click and change events on elements with data-append-url attributes
 * @returns {void}
 */
const initializeUrlAppender = () => {
  try {
    document.addEventListener('click', handleClickWithAppendUrl);
    document.addEventListener('change', handleChangeWithAppendUrl);
    console.log('URL Appender: Event listeners initialized (click and change)');
  } catch (error) {
    console.error(ERROR_MESSAGES.INIT, error);
  }
};

/**
 * Reinitialize URL appender (useful for dynamically added content)
 * @param {HTMLElement} container - Optional container to search within
 * @returns {void}
 */
const reinitializeUrlAppender = (container = document) => {
  console.log('URL Appender: Using event-driven approach, no reinitialization needed');
};

// Export functions for use in other modules
export { initializeUrlAppender, reinitializeUrlAppender, appendUrlToElement, removeIssueFromUrl };
